import { notification } from "antd"
import { AxiosError } from "axios"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { extractApiErrorMessage } from "../../../helpers/apiErrorMessage"
import useFeatureFlag from "../../../hooks/useFeatureFlag"
import { SetRecoveryComponentRequest, getRecoveryComponentSettings, setRecoveryComponentEnabled } from "./recoveryComponentApi"

export const DUAL_PRICING_FEATURE = 'dual-pricing'

const recoveryComponentKey = ['invoicing-settings-recovery-component']

export const isRecoverySettingsForbidden = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status
  return status === 401 || status === 403
}

export const useRecoveryComponentQuery = () => {
  const isFeatureEnabled = useFeatureFlag(DUAL_PRICING_FEATURE)

  return useQuery(recoveryComponentKey, {
    queryFn: getRecoveryComponentSettings,
    enabled: isFeatureEnabled,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    onError(error: unknown) {
      if (isRecoverySettingsForbidden(error)) {
        return
      }
      notification.error({ message: 'There was a problem loading your payment fee settings.' })
    }
  })
}

export const useIsRecoveryComponentEnabled = (): boolean => {
  const settings = useRecoveryComponentQuery().data
  return settings?.featureEnabled === true && settings.enabled
}

export const useSetRecoveryComponentEnabledMutation = (onFailure: (message: string) => void) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SetRecoveryComponentRequest) => setRecoveryComponentEnabled(request),
    onSuccess: () => {
      return queryClient.invalidateQueries(recoveryComponentKey)
    },
    onError(error: unknown) {
      onFailure(extractApiErrorMessage(error, 'There was a problem updating your payment fee settings.'))
    }
  })
}
